import { defineStore } from "pinia";
import { ref } from "vue";
import requests from "@/services/requests";
import response from "@/services/response";
import sessionService from "@/services/session";
import axios from "axios";
import { clear } from "idb-keyval";
import { useBannersStore } from "@/stores/banners";
import { useCnxtSessionStore } from "@/stores/cnxtSession";
import { useIndoStore } from "@/stores/indo";

const STORAGE_KEY = "auth";

// Tokens tal y como están en localStorage, que comparten todas las pestañas.
function leerTokensPersistidos() {
  try {
    const { token, refreshToken } = JSON.parse(localStorage.getItem(STORAGE_KEY)) ?? {};
    return { token, refreshToken };
  } catch {
    return {};
  }
}

// Serializa la renovación entre pestañas (Web Locks); sin soporte, sólo en esta.
function conBloqueoEntrePestanas(fn) {
  return navigator.locks?.request ? navigator.locks.request("indo-renovar-token", fn) : fn();
}

// La cabecera por defecto puede ser la de IndoManager: sólo se cambia si
// llevaba el token de usuario anterior.
function renovarCabeceraPorDefecto(tokenAnterior, tokenNuevo) {
  const cabecera = axios.defaults.headers.common["Authorization"];
  if (!cabecera || cabecera === `Bearer ${tokenAnterior}`) {
    axios.defaults.headers.common["Authorization"] = `Bearer ${tokenNuevo}`;
  }
}

// Otra pestaña ha rotado los tokens o cerrado sesión: el refresh token sólo
// sirve una vez, así que esta pestaña debe adoptar los nuevos (y no
// sobrescribirlos con los suyos al persistir) o salir de la sesión.
export function sincronizarEntrePestanas(onSesionCerrada) {
  window.addEventListener("storage", (event) => {
    if (event.storageArea !== localStorage) return;
    if (event.key !== STORAGE_KEY && event.key !== null) return;
    const auth = useAuthStore();
    const tokenAnterior = auth.token;
    auth.$hydrate();
    if (auth.token) renovarCabeceraPorDefecto(tokenAnterior, auth.token);
    else onSesionCerrada();
  });
}

export const useAuthStore = defineStore(
  "auth",
  () => {
    const username = ref(null);
    const token = ref(null);
    const tokenManager = ref(null);
    const isAuthManager = ref(false);
    const indoManager = ref(null);
    const tempPassword = ref(null);
    // Pese al nombre, es la última actividad compartida entre pestañas (ver
    // sessionService.ultimaActividad); se mantiene por el estado persistido.
    const loginTimestamp = ref(null);
    // Inicio de la sesión (máximo de 30 días, ver sessionService). Sólo lo
    // fija Login y lo borra Logout: RenovarToken no lo toca.
    const sesionIniciadaEn = ref(null);
    // Llega con el login (Autenticar) y rota en cada RenovarToken: cada uno
    // sólo sirve una vez.
    const refreshToken = ref(null);
    let renovacionEnCurso = null;

    const isAuthenticated = () => !!token.value;
    const isAuthManagerGetter = () => !!tokenManager.value;

    function aplicarTokens(nuevoToken, nuevoRefresh) {
      renovarCabeceraPorDefecto(token.value, nuevoToken);
      token.value = nuevoToken;
      refreshToken.value = nuevoRefresh;
    }

    // Pide un token nuevo con el refresh token. Una sola renovación a la vez
    // en la pestaña y, con Web Locks, entre pestañas: si otra ya ha rotado el
    // refresh se adoptan sus tokens en lugar de gastar el nuestro, ya
    // consumido. Si falla (returnValue 1 = sesión no válida, -1 = error
    // técnico, o sin respuesta) no se reintenta, porque el refresh puede
    // haber quedado consumido: el caller debe mandar al login.
    function RenovarToken() {
      if (!renovacionEnCurso) {
        const refreshUsado = refreshToken.value;
        renovacionEnCurso = conBloqueoEntrePestanas(() => renovar(refreshUsado)).finally(() => {
          renovacionEnCurso = null;
        });
      }
      return renovacionEnCurso;
    }

    async function renovar(refreshUsado) {
      if (!refreshUsado) throw new Error("Sin refresh token");
      const persistidos = leerTokensPersistidos();
      if (persistidos.refreshToken && persistidos.refreshToken !== refreshUsado) {
        aplicarTokens(persistidos.token, persistidos.refreshToken);
        return;
      }
      try {
        const data = response.renovarToken(await requests.renovarToken(refreshUsado));
        if (data.returnValue !== 0 || !data.Token || !data.RefreshToken) {
          throw new Error(data.MensajeError || `RenovarToken: returnValue ${data.returnValue}`);
        }
        aplicarTokens(data.Token, data.RefreshToken);
      } catch (error) {
        // Inválido o consumido: que el logout no lo envíe a CerrarSesion.
        refreshToken.value = null;
        throw error;
      }
    }

    async function Login({ username: user, password }) {
      try {
        delete axios.defaults.headers.common["Authorization"];

        // SAP exige el usuario en mayúsculas; normalizamos aquí para que el
        // usuario pueda escribirlo en cualquier caso sin romper el login.
        const normalizedUser = String(user ?? "").trim().toUpperCase();
        const data = await requests.login(normalizedUser, password);
        const loginData = response.login(data);

        if (loginData.returnValue === 0 || loginData.returnValue === 20) {
          await clear();

          token.value = loginData.Token;
          refreshToken.value = loginData.RefreshToken ?? null;
          username.value = normalizedUser;
          loginTimestamp.value = Date.now();
          sesionIniciadaEn.value = Date.now();

          if (!loginData.Token) {
          } else {
            axios.defaults.headers.common["Authorization"] = `Bearer ${loginData.Token}`;
          }

          sessionService.startSessionTimer();

          const needChangePassword = loginData.CodigoRetorno === 20 || loginData.returnValue === 20;
          if (needChangePassword) tempPassword.value = password;

          return needChangePassword ? 20 : 0;
        }
        throw new Error(loginData.MensajeErrorSAP);
      } catch (error) {
        localStorage.removeItem("savedCredentials");
        throw error.message;
      }
    }

    async function Logout() {
      // Invalida el refresh en el servidor antes de limpiar. Se saca del
      // estado primero para que nada lo use mientras tanto.
      const refreshPendiente = refreshToken.value;
      refreshToken.value = null;
      // Si falla, el refresh caducará solo en el servidor: se cierra igual.
      if (refreshPendiente) await requests.cerrarSesion(refreshPendiente).catch(() => {});

      try {
        sessionService.clearSessionTimer();

        useBannersStore().resetQueues();
        // La sesión CNXT/Mimetika es del usuario: no debe heredarla el siguiente login
        useCnxtSessionStore().disconnect();

        token.value = null;
        username.value = null;
        loginTimestamp.value = null;
        sesionIniciadaEn.value = null;
        tempPassword.value = null;
        tokenManager.value = null;
        isAuthManager.value = false;
        indoManager.value = null;

        useIndoStore().emptyData();

        await clear();

        const userPreferences = {};
        Object.keys(localStorage).forEach((key) => {
          if (
            key.startsWith("indo-bonos-news-popup-show-") ||
            key.startsWith("indo-notificaciones-") ||
            key.startsWith("indo-bonos-send-information-") ||
            key.startsWith("notificaciones-emergentes-ocultar-todo-") ||
            key.startsWith("notificaciones-emergentes-ocultas-") ||
            key === "readAlerts" ||
            key === "app_locale"
          ) {
            userPreferences[key] = localStorage.getItem(key);
          }
        });

        localStorage.removeItem("savedCredentials");
        localStorage.removeItem("vuex");
        localStorage.clear();

        Object.keys(userPreferences).forEach((key) => {
          localStorage.setItem(key, userPreferences[key]);
        });

        sessionStorage.clear();
        delete axios.defaults.headers.common["Authorization"];
      } catch (error) {
        try {
          const userPreferences = {};
          Object.keys(localStorage).forEach((key) => {
            if (
              key.startsWith("indo-bonos-news-popup-show-") ||
              key.startsWith("indo-notificaciones-") ||
              key.startsWith("indo-bonos-send-information-") ||
              key.startsWith("notificaciones-emergentes-ocultar-todo-") ||
              key.startsWith("notificaciones-emergentes-ocultas-") ||
              key === "app_locale"
            ) {
              userPreferences[key] = localStorage.getItem(key);
            }
          });
          localStorage.clear();
          sessionStorage.clear();
          Object.keys(userPreferences).forEach((key) =>
            localStorage.setItem(key, userPreferences[key]),
          );
        } catch (preserveError) {
          localStorage.clear();
          sessionStorage.clear();
        }
        delete axios.defaults.headers.common["Authorization"];
      }
    }

    async function CambioPassword({ username: user, password, newPassword }) {
      const normalizedUser = String(user ?? "").trim().toUpperCase();
      const data = await requests.cambioPassword(normalizedUser, password, newPassword, token.value);
      const authData = response.cambioPassword(data);
      if (authData.returnValue !== 0) return authData.MensajeErrorSAP;
      return null;
    }

    async function LoginManager({ username: user, password }) {
      try {
        const normalizedUser = String(user ?? "").trim().toUpperCase();
        const data = await requests.loginManager(normalizedUser, password);
        const authData = response.loginManager(data);

        if (authData.returnValue !== 0) {
          const errorMessage =
            authData.ExportReturnMessage ||
            authData.oDatosCliente?.sMensajeSAP ||
            "Login incorrecto";
          throw new Error(errorMessage);
        }
        if (!authData.Token) throw new Error("Error de autenticación: No se recibió token");

        tokenManager.value = authData.Token;
        isAuthManager.value = true;
        indoManager.value = authData.oDatosCliente;
        axios.defaults.headers.common["Authorization"] = `Bearer ${authData.Token}`;
      } catch (error) {
        throw error;
      }
    }

    async function ManagerLogout() {
      tokenManager.value = null;
      isAuthManager.value = false;
      indoManager.value = null;
    }

    async function PedirContrasena({ cliente }) {
      const data = await requests.pedirContrasena(cliente);
      const authData = response.pedirContrasena(data);
      if (authData.returnValue === 0 && authData.sEmailCliente)
        return { success: true, sEmailCliente: authData.sEmailCliente };
      return { success: false };
    }

    async function CambiarContrasena({ cliente, actual, nueva }) {
      const data = await requests.cambiarContrasena(token.value, cliente, actual, nueva);
      const authData = response.cambiarContrasena(data);
      if (authData.returnValue === 0) return true;
      return authData;
    }

    return {
      username,
      token,
      tokenManager,
      isAuthManager,
      indoManager,
      tempPassword,
      loginTimestamp,
      sesionIniciadaEn,
      refreshToken,
      isAuthenticated,
      isAuthManagerGetter,
      Login,
      RenovarToken,
      Logout,
      CambioPassword,
      LoginManager,
      ManagerLogout,
      PedirContrasena,
      CambiarContrasena,
    };
  },
  {
    // `pick` (v4 del plugin; `paths` era v3 y se ignoraba, persistiendo todo).
    // `tempPassword` queda sólo en memoria.
    persist: {
      key: STORAGE_KEY,
      storage: localStorage,
      pick: [
        "username",
        "token",
        "refreshToken",
        "tokenManager",
        "isAuthManager",
        "indoManager",
        "loginTimestamp",
        "sesionIniciadaEn",
      ],
    },
  },
);
